/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { call, put, takeLatest, delay } from 'redux-saga/effects';

import apiNew from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import {
 deleteCookieCustom,
 getCookieCustom,
 setCookieCustom,
} from '../../utils/cookies';
import {
 AccessGroupsDTO,
 CreateUpdateUsersDTO,
 FilterDTO,
 GroupDTO,
 JWTRefreshTokenSubmitDTO,
 JWTTokenDTO,
 LoginTwoFAVerifyDTO,
 RolesDTO,
 TwoFAVerifyDTO,
 UsersDTO,
 VerifyDTO,
} from './identityTypes';
import * as commonSagas from '../_common/commonSagas';
import {
 getTableStart,
 getItemStart,
 createItemStart,
 updateItemStart,
 deleteItemStart,
 getTableStructureStart,
 getItemDocumentsStart,
 uploadItemDocumentsStart,
 deleteItemDocumentStart,
 getTableExcelStart,
 identityLoginFailure,
 identityLoginStart,
 identityLoginSuccess,
 identityLogoutStart,
 identityLogoutSuccess,
 identityLogoutFailure,
 identityRefreshTokenStart,
 identityRefreshTokenSuccess,
 identityRefreshTokenFailure,
 identityMicrosoftStart,
 identityMicrosoftSuccess,
 identityMicrosoftFailure,
 identityMicrosoftCallbackStart,
 identityMicrosoftCallbackSuccess,
 identityMicrosoftCallbackFailure,
 identityGoogleStart,
 identityGoogleSuccess,
 identityGoogleFailure,
 identityGoogleCallbackStart,
 identityGoogleCallbackSuccess,
 identityGoogleCallbackFailure,
 identityVerify2FAStart,
 identityVerify2FASuccess,
 identityVerify2FAFailure,
 identityGenerate2FACodeStart,
 identityGenerate2FACodeSuccess,
 identityGenerate2FACodeFailure,
 identitySetVerificationStart,
 identitySetVerificationSuccess,
 identitySetVerificationFailure,
 identityConfirmVerificationStart,
 identityConfirmVerificationSuccess,
 identityConfirmVerificationFailure,
 identitySetTotpStart,
 identitySetTotpSuccess,
 identitySetTotpFailure,
 identityVerifyTotpStart,
 identityVerifyTotpSuccess,
 identityVerifyTotpFailure,
 identityGetSelfStart,
 identityGetSelfSuccess,
 identityGetSelfFailure,
 identityUpdateSelfStart,
 identityUpdateSelfSuccess,
 identityUpdateSelfFailure,
 identityGetRolesStart,
 identityGetRolesSuccess,
 identityGetRolesFailure,
 identityGetItemRolesStart,
 identityGetItemRolesSuccess,
 identityGetItemRolesFailure,
 identityGetItemGroupRolesStart,
 identityGetItemGroupRolesSuccess,
 identityGetItemGroupRolesFailure,
 identityAddRoleToItemGroupStart,
 identityAddRoleToItemGroupSuccess,
 identityAddRoleToItemGroupFailure,
 identityCreateRoleStart,
 identityCreateRoleSuccess,
 identityCreateRoleFailure,
 identityCreateAccessGroupStart,
 identityCreateAccessGroupSuccess,
 identityCreateAccessGroupFailure,
 identityDeleteRoleStart,
 identityDeleteRoleSuccess,
 identityDeleteRoleFailure,
 identityRemoveRoleFromGroupStart,
 identityRemoveRoleFromGroupSuccess,
 identityRemoveRoleFromGroupFailure,
 identityRevokeTokenStart,
 identityRevokeTokenSuccess,
 identityRevokeTokenFailure,
 identityRevokeAllTokensStart,
 identityRevokeAllTokensSuccess,
 identityRevokeAllTokensFailure,
 identityGetSelfGroupAccessStart,
 identityGetSelfGroupAccessSuccess,
 identityGetSelfGroupAccessFailure,
 identityGetSelfRolesAccessStart,
 identityGetSelfRolesAccessSuccess,
 identityGetSelfRolesAccessFailure,
 identityGetSelfGroupsAccessStart,
 identityGetSelfGroupsAccessSuccess,
 identityGetSelfGroupsAccessFailure,
 identityGetItemGroupsStart,
 identityGetItemGroupsSuccess,
 identityGetItemGroupsFailure,
 identityGetItemGroupStart,
 identityGetItemGroupSuccess,
 identityGetItemGroupFailure,
 identityCreateItemGroupStart,
 identityCreateItemGroupSuccess,
 identityCreateItemGroupFailure,
 identityUpdateItemGroupStart,
 identityUpdateItemGroupSuccess,
 identityUpdateItemGroupFailure,
 identityUpdateSelfFinish,
 identityGetSelfFinish,
 identityGetItemSuccess,
 identityGetItemFailure,
 identityGenerateApiKeyStart,
 identityGenerateApiKeySuccess,
 identityGenerateApiKeyFailure,
} from './identitySlice';

function* identityLoginSaga(
 props: PayloadAction<{ email: string; password: string }>,
) {
 try {
  const response: AxiosResponse<JWTTokenDTO> = yield call(
   apiNew.post,
   '/Identity/v1/login',
   { Email: props.payload.email, Password: props.payload.password },
  );

  // ############################
  // REFACTOR OTHER LOGIN TYPES SUCCESSES
  // ############################
  const {
   access_token,
   refresh_token,
   expiration,
   configuration,
   requires2FA,
   sessionId,
   methods2FA,
  } = response.data;

  if (access_token) {
   const decoded = jwtDecode(access_token);
   setCookieCustom('user', JSON.stringify(decoded), expiration || 3600);
   setCookieCustom('access_token', access_token, expiration || 3600);
   setCookieCustom('refresh_token', refresh_token || '', expiration || 3600);
   setCookieCustom(
    'expiration',
    (expiration || 3600).toString(),
    expiration || 3600,
   );
   setCookieCustom('user_id', (decoded as any)?.userId, expiration || 3600);
   if (configuration) {
    localStorage.setItem('configuration', JSON.stringify(configuration));
   }
  }

  yield put(identityLoginSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityLoginFailure);
 }
}

function* identityLogoutSaga() {
 try {
  const response: AxiosResponse<any> = yield call(
   apiNew.post,
   '/Identity/v1/logout',
   {},
  );

  deleteCookieCustom('access_token', '/');
  deleteCookieCustom('refresh_token', '/');
  deleteCookieCustom('expiration', '/');
  deleteCookieCustom('user_id', '/');
  deleteCookieCustom('configuration', '/');

  yield put(identityLogoutSuccess(response.data));
  window.location.href = '/login';
 } catch (error) {
  yield* errorHandling(error, identityLogoutFailure);
 }
}

function* identityRefreshTokenSaga(
 props: PayloadAction<JWTRefreshTokenSubmitDTO>,
) {
 try {
  const response: AxiosResponse<JWTTokenDTO> = yield call(
   apiNew.post,
   '/Identity/v1/refresh-token',
   {
    refresh_token: props.payload.refresh_token,
    connectionKey: props.payload.connectionKey,
   },
  );
  const { access_token, refresh_token, expiration, configuration } =
   response.data;

  if (access_token) {
   const decoded = jwtDecode(access_token);
   setCookieCustom('user', JSON.stringify(decoded), expiration || 3600);
   setCookieCustom('access_token', access_token, expiration || 3600);
   setCookieCustom('user_id', (decoded as any)?.userId, expiration || 3600);
  }

  if (configuration) {
   localStorage.setItem('configuration', JSON.stringify(configuration));
  }

  yield put(identityRefreshTokenSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityRefreshTokenFailure);
 }
}

function* identityMicrosoftSaga(
 props: PayloadAction<{ redirectUri?: string }>,
) {
 try {
  const params = props.payload.redirectUri
   ? `?redirectUri=${encodeURIComponent(props.payload.redirectUri)}`
   : '';
  const response: AxiosResponse<{ uri: string }> = yield call(
   apiNew.get,
   `/Identity/v1/microsoft${params}`,
  );
  yield put(identityMicrosoftSuccess({ uri: response.data.uri }));
 } catch (error) {
  yield* errorHandling(error, identityMicrosoftFailure);
 }
}

function* identityMicrosoftCallbackSaga(
 props: PayloadAction<{ code?: string; redirectUri?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.code) params.append('code', props.payload.code);
  if (props.payload.redirectUri)
   params.append('redirectUri', props.payload.redirectUri);
  const queryString = params.toString();
  const url = `/Identity/v1/microsoft-callback${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<JWTTokenDTO> = yield call(apiNew.get, url);

  const { access_token, refresh_token, expiration, configuration } =
   response.data;

  if (access_token) {
   const decoded = jwtDecode(access_token);
   setCookieCustom('user', JSON.stringify(decoded), expiration || 3600);
   setCookieCustom('access_token', access_token, expiration || 3600);
   setCookieCustom('refresh_token', refresh_token || '', expiration || 3600);
   setCookieCustom(
    'expiration',
    (expiration || 3600).toString(),
    expiration || 3600,
   );
   setCookieCustom('user_id', (decoded as any)?.userId, expiration || 3600);
   if (configuration) {
    localStorage.setItem('configuration', JSON.stringify(configuration));
   }
  }

  yield put(identityMicrosoftCallbackSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityMicrosoftCallbackFailure);
 }
}

function* identityGoogleSaga(props: PayloadAction<{ redirectUri?: string }>) {
 try {
  const params = props.payload.redirectUri
   ? `?redirectUri=${encodeURIComponent(props.payload.redirectUri)}`
   : '';
  const response: AxiosResponse<{ uri: string }> = yield call(
   apiNew.get,
   `/Identity/v1/google${params}`,
  );

  yield put(identityGoogleSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGoogleFailure);
 }
}

function* identityGoogleCallbackSaga(
 props: PayloadAction<{ code?: string; redirectUri?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.code) params.append('code', props.payload.code);
  if (props.payload.redirectUri)
   params.append('redirectUri', props.payload.redirectUri);
  const queryString = params.toString();
  const url = `/Identity/v1/google-callback${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<JWTTokenDTO> = yield call(apiNew.get, url);

  const { access_token, refresh_token, expiration, configuration } =
   response.data;

  if (access_token) {
   const decoded = jwtDecode(access_token);
   setCookieCustom('user', JSON.stringify(decoded), expiration || 3600);
   setCookieCustom('access_token', access_token, expiration || 3600);
   setCookieCustom('refresh_token', refresh_token || '', expiration || 3600);
   setCookieCustom(
    'expiration',
    (expiration || 3600).toString(),
    expiration || 3600,
   );
   setCookieCustom('user_id', (decoded as any)?.userId, expiration || 3600);
   if (configuration) {
    localStorage.setItem('configuration', JSON.stringify(configuration));
   }
  }

  yield put(identityGoogleCallbackSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGoogleCallbackFailure);
 }
}

function* identityVerify2FASaga(props: PayloadAction<LoginTwoFAVerifyDTO>) {
 try {
  const response: AxiosResponse<JWTTokenDTO> = yield call(
   apiNew.post,
   '/Identity/v1/login/verify-2fa',
   props.payload,
  );

  const { access_token, refresh_token, expiration, configuration } =
   response.data;

  if (access_token) {
   const decoded = jwtDecode(access_token);
   setCookieCustom('user', JSON.stringify(decoded), expiration || 3600);
   setCookieCustom('access_token', access_token, expiration || 3600);
   setCookieCustom('refresh_token', refresh_token || '', expiration || 3600);
   setCookieCustom(
    'expiration',
    (expiration || 3600).toString(),
    expiration || 3600,
   );
   setCookieCustom('user_id', (decoded as any)?.userId, expiration || 3600);
   if (configuration) {
    localStorage.setItem('configuration', JSON.stringify(configuration));
   }
  }

  yield put(identityVerify2FASuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityVerify2FAFailure);
 }
}

function* identityGenerate2FACodeSaga(props: PayloadAction<TwoFAVerifyDTO>) {
 try {
  console.log('identityGenerate2FACodeSaga', props.payload);
  const response: AxiosResponse<string> = yield call(
   apiNew.post,
   '/Identity/v1/login/verify-2fa/generatecode',
   props.payload,
  );
  console.log('identityGenerate2FACodeSaga response', response.data);
  yield put(identityGenerate2FACodeSuccess({ uri: response.data }));
 } catch (error) {
  yield* errorHandling(error, identityGenerate2FACodeFailure);
 }
}

function* identitySetVerificationSaga(props: PayloadAction<VerifyDTO>) {
 try {
  const response: AxiosResponse<string> = yield call(
   apiNew.post,
   '/Identity/v1/setVerification',
   props.payload,
  );
  yield put(identitySetVerificationSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identitySetVerificationFailure);
 }
}

function* identityConfirmVerificationSaga(
 props: PayloadAction<{
  sessionId?: string;
  userid?: string;
  method?: 1 | 2 | 3;
  code?: string;
 }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.sessionId)
   params.append('sessionId', props.payload.sessionId);
  if (props.payload.userid) params.append('userid', props.payload.userid);
  if (props.payload.method)
   params.append('method', props.payload.method.toString());
  if (props.payload.code) params.append('code', props.payload.code);
  const queryString = params.toString();
  const url = `/Identity/v1/confirmVerification${
   queryString ? `?${queryString}` : ''
  }`;
  const response: AxiosResponse<string> = yield call(apiNew.get, url);
  yield put(identityConfirmVerificationSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityConfirmVerificationFailure);
 }
}

function* identitySetTotpSaga(
 props: PayloadAction<{ userid?: string; sessionId?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.userid) params.append('userid', props.payload.userid);
  if (props.payload.sessionId)
   params.append('sessionId', props.payload.sessionId);
  const queryString = params.toString();
  const url = `/Identity/v1/setTotp${queryString ? `?${queryString}` : ''}`;
  const response: AxiosResponse<{ uri: string }> = yield call(apiNew.get, url);
  yield put(identitySetTotpSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identitySetTotpFailure);
 }
}

function* identityVerifyTotpSaga(
 props: PayloadAction<{ userid?: string; sessionId?: string; code?: string }>,
) {
 try {
  const params = new URLSearchParams();
  if (props.payload.userid) params.append('userid', props.payload.userid);
  if (props.payload.sessionId)
   params.append('sessionId', props.payload.sessionId);
  if (props.payload.code) params.append('code', props.payload.code);
  const queryString = params.toString();
  const url = `/Identity/v1/verifyTotp${queryString ? `?${queryString}` : ''}`;
  const response: AxiosResponse<string> = yield call(apiNew.post, url, {});
  yield put(identityVerifyTotpSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityVerifyTotpFailure);
 }
}

function* identityGetSelfSaga(props: PayloadAction<{ fields?: string }>) {
 try {
  const params = props.payload.fields
   ? `?fields=${encodeURIComponent(props.payload.fields)}`
   : '';
  const response: AxiosResponse<UsersDTO> = yield call(
   apiNew.get,
   `/Identity/v1/users/self${params}`,
  );
  yield put(identityGetSelfSuccess(response.data));
  yield delay(0);
  yield put(identityGetSelfFinish());
 } catch (error) {
  yield* errorHandling(error, identityGetSelfFailure);
 }
}

function* identityUpdateSelfSaga(props: PayloadAction<CreateUpdateUsersDTO>) {
 try {
  const response: AxiosResponse<UsersDTO> = yield call(
   apiNew.put,
   '/Identity/v1/users/self/update',
   props.payload,
  );
  yield put(identityUpdateSelfSuccess(response.data));
  yield delay(0);
  yield put(identityUpdateSelfFinish());
 } catch (error) {
  yield* errorHandling(error, identityUpdateSelfFailure);
 }
}

function* identityGetRolesSaga(props: PayloadAction<FilterDTO>) {
 try {
  const response: AxiosResponse<RolesDTO[]> = yield call(
   apiNew.post,
   '/Identity/v1/roles',
   props.payload,
  );
  yield put(identityGetRolesSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetRolesFailure);
 }
}

function* identityGetItemRolesSaga(props: PayloadAction<{ userId: string }>) {
 try {
  const response: AxiosResponse<AccessGroupsDTO[]> = yield call(
   apiNew.get,
   `/Identity/v1/${props.payload.userId}/roles`,
  );
  yield put(identityGetItemRolesSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetItemRolesFailure);
 }
}

function* identityGetItemGroupRolesSaga(
 props: PayloadAction<{ userId: string; groupId: string }>,
) {
 try {
  const response: AxiosResponse<RolesDTO[]> = yield call(
   apiNew.get,
   `/Identity/v1/${props.payload.userId}/${props.payload.groupId}/roles`,
  );
  yield put(identityGetItemGroupRolesSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetItemGroupRolesFailure);
 }
}

function* identityAddRoleToItemGroupSaga(
 props: PayloadAction<{ userId: string; groupId: string; role: RolesDTO }>,
) {
 try {
  yield call(
   apiNew.post,
   `/Identity/v1/${props.payload.userId}/${props.payload.groupId}/addrole`,
   props.payload.role,
  );
  yield put(identityAddRoleToItemGroupSuccess());
 } catch (error) {
  yield* errorHandling(error, identityAddRoleToItemGroupFailure);
 }
}

function* identityCreateRoleSaga(props: PayloadAction<RolesDTO>) {
 try {
  const response: AxiosResponse<RolesDTO> = yield call(
   apiNew.post,
   '/Identity/v1/createrole',
   props.payload,
  );
  yield put(identityCreateRoleSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityCreateRoleFailure);
 }
}

function* identityCreateAccessGroupSaga(props: PayloadAction<AccessGroupsDTO>) {
 try {
  const response: AxiosResponse<AccessGroupsDTO> = yield call(
   apiNew.post,
   '/Identity/v1/createaccessgroup',
   props.payload,
  );
  yield put(identityCreateAccessGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityCreateAccessGroupFailure);
 }
}

function* identityDeleteRoleSaga(props: PayloadAction<RolesDTO>) {
 try {
  yield call(apiNew.del, '/Identity/v1/deleterole', props.payload);
  yield put(identityDeleteRoleSuccess());
 } catch (error) {
  yield* errorHandling(error, identityDeleteRoleFailure);
 }
}

function* identityRemoveRoleFromGroupSaga(
 props: PayloadAction<{ groupId: string; role: RolesDTO }>,
) {
 try {
  yield call(
   apiNew.del,
   `/Identity/v1/${props.payload.groupId}/removerole`,
   props.payload.role,
  );
  yield put(identityRemoveRoleFromGroupSuccess());
 } catch (error) {
  yield* errorHandling(error, identityRemoveRoleFromGroupFailure);
 }
}

function* identityRevokeTokenSaga(props: PayloadAction<{ Id: string }>) {
 try {
  yield call(apiNew.post, `/Identity/v1/revoke/${props.payload.Id}`, {});
  yield put(identityRevokeTokenSuccess());
 } catch (error) {
  yield* errorHandling(error, identityRevokeTokenFailure);
 }
}

function* identityRevokeAllTokensSaga() {
 try {
  yield call(apiNew.post, '/Identity/v1/revoke-all', {});
  yield put(identityRevokeAllTokensSuccess());
 } catch (error) {
  yield* errorHandling(error, identityRevokeAllTokensFailure);
 }
}

function* identityGetSelfGroupAccessSaga(
 props: PayloadAction<{ groupId: string }>,
) {
 try {
  const response: AxiosResponse<AccessGroupsDTO[]> = yield call(
   apiNew.get,
   `/Identity/v1/self/${props.payload.groupId}/access`,
  );
  yield put(identityGetSelfGroupAccessSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetSelfGroupAccessFailure);
 }
}

function* identityGetSelfRolesAccessSaga() {
 try {
  const response: AxiosResponse<RolesDTO[]> = yield call(
   apiNew.get,
   '/Identity/v1/roles/self/access',
  );
  yield put(identityGetSelfRolesAccessSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetSelfRolesAccessFailure);
 }
}

function* identityGetSelfGroupsAccessSaga() {
 try {
  const response: AxiosResponse<AccessGroupsDTO[]> = yield call(
   apiNew.get,
   '/Identity/v1/groups/self/access',
  );
  yield put(identityGetSelfGroupsAccessSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetSelfGroupsAccessFailure);
 }
}

function* identityGetItemGroupsSaga(props: PayloadAction<FilterDTO>) {
 try {
  const response: AxiosResponse<GroupDTO[]> = yield call(
   apiNew.post,
   '/Identity/v1/usergroups',
   props.payload,
  );
  yield put(identityGetItemGroupsSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetItemGroupsFailure);
 }
}

function* identityGetItemGroupSaga(
 props: PayloadAction<{ id: number; fields?: string }>,
) {
 try {
  const params = props.payload.fields
   ? `?fields=${encodeURIComponent(props.payload.fields)}`
   : '';
  const response: AxiosResponse<GroupDTO> = yield call(
   apiNew.get,
   `/Identity/v1/usergroups/${props.payload.id}${params}`,
  );
  yield put(identityGetItemGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetItemGroupFailure);
 }
}

function* identityCreateItemGroupSaga(props: PayloadAction<any>) {
 try {
  const response: AxiosResponse<GroupDTO> = yield call(
   apiNew.post,
   '/Identity/v1/usergroups/create',
   props.payload,
  );
  yield put(identityCreateItemGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityCreateItemGroupFailure);
 }
}

function* identityUpdateItemGroupSaga(
 props: PayloadAction<{ id: number; data: any }>,
) {
 try {
  const response: AxiosResponse<GroupDTO> = yield call(
   apiNew.put,
   `/Identity/v1/usergroups/update/${props.payload.id}`,
   props.payload.data,
  );
  yield put(identityUpdateItemGroupSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityUpdateItemGroupFailure);
 }
}

function* identityGetItemSaga(props: PayloadAction<any>) {
 try {
  const response: AxiosResponse<UsersDTO> = yield call(
   apiNew.get,
   `/Identity/v1/users/${props.payload.id}`,
  );
  yield put(identityGetItemSuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGetItemFailure);
 }
}

function* identityGenerateApiKeySaga(props: PayloadAction<{ userId: string }>) {
 try {
  const response: AxiosResponse<{ apiKey: string }> = yield call(
   apiNew.get,
   `/Identity/v1/users/${props.payload.userId}/generateapikey`,
  );
  yield put(identityGenerateApiKeySuccess(response.data));
 } catch (error) {
  yield* errorHandling(error, identityGenerateApiKeyFailure);
 }
}

export function* watchIdentitySaga() {
 yield takeLatest(getTableStart.type, commonSagas.getTable);
 yield takeLatest(getItemStart.type, identityGetItemSaga);
 yield takeLatest(createItemStart.type, commonSagas.createItem);
 yield takeLatest(updateItemStart.type, commonSagas.updateItem);
 yield takeLatest(deleteItemStart.type, commonSagas.deleteItem);
 yield takeLatest(getTableStructureStart.type, commonSagas.getTableStructure);
 yield takeLatest(getItemDocumentsStart.type, commonSagas.getItemDocuments);
 yield takeLatest(
  uploadItemDocumentsStart.type,
  commonSagas.uploadItemDocuments,
 );
 yield takeLatest(deleteItemDocumentStart.type, commonSagas.deleteItemDocument);
 yield takeLatest(getTableExcelStart.type, commonSagas.getTableExcel);
 yield takeLatest(identityLoginStart.type, identityLoginSaga);
 yield takeLatest(identityLogoutStart.type, identityLogoutSaga);
 yield takeLatest(identityRefreshTokenStart.type, identityRefreshTokenSaga);
 yield takeLatest(identityMicrosoftStart.type, identityMicrosoftSaga);
 yield takeLatest(
  identityMicrosoftCallbackStart.type,
  identityMicrosoftCallbackSaga,
 );
 yield takeLatest(identityGoogleStart.type, identityGoogleSaga);
 yield takeLatest(identityGoogleCallbackStart.type, identityGoogleCallbackSaga);
 yield takeLatest(identityVerify2FAStart.type, identityVerify2FASaga);
 yield takeLatest(
  identityGenerate2FACodeStart.type,
  identityGenerate2FACodeSaga,
 );
 yield takeLatest(
  identitySetVerificationStart.type,
  identitySetVerificationSaga,
 );
 yield takeLatest(
  identityConfirmVerificationStart.type,
  identityConfirmVerificationSaga,
 );
 yield takeLatest(identitySetTotpStart.type, identitySetTotpSaga);
 yield takeLatest(identityVerifyTotpStart.type, identityVerifyTotpSaga);
 yield takeLatest(identityGetSelfStart.type, identityGetSelfSaga);
 yield takeLatest(identityUpdateSelfStart.type, identityUpdateSelfSaga);
 yield takeLatest(identityGetRolesStart.type, identityGetRolesSaga);
 yield takeLatest(identityGetItemRolesStart.type, identityGetItemRolesSaga);
 yield takeLatest(
  identityGetItemGroupRolesStart.type,
  identityGetItemGroupRolesSaga,
 );
 yield takeLatest(
  identityAddRoleToItemGroupStart.type,
  identityAddRoleToItemGroupSaga,
 );
 yield takeLatest(identityCreateRoleStart.type, identityCreateRoleSaga);
 yield takeLatest(
  identityCreateAccessGroupStart.type,
  identityCreateAccessGroupSaga,
 );
 yield takeLatest(identityDeleteRoleStart.type, identityDeleteRoleSaga);
 yield takeLatest(
  identityRemoveRoleFromGroupStart.type,
  identityRemoveRoleFromGroupSaga,
 );
 yield takeLatest(identityRevokeTokenStart.type, identityRevokeTokenSaga);
 yield takeLatest(
  identityRevokeAllTokensStart.type,
  identityRevokeAllTokensSaga,
 );
 yield takeLatest(
  identityGetSelfGroupAccessStart.type,
  identityGetSelfGroupAccessSaga,
 );
 yield takeLatest(
  identityGetSelfRolesAccessStart.type,
  identityGetSelfRolesAccessSaga,
 );
 yield takeLatest(
  identityGetSelfGroupsAccessStart.type,
  identityGetSelfGroupsAccessSaga,
 );
 yield takeLatest(identityGetItemGroupsStart.type, identityGetItemGroupsSaga);
 yield takeLatest(identityGetItemGroupStart.type, identityGetItemGroupSaga);
 yield takeLatest(
  identityCreateItemGroupStart.type,
  identityCreateItemGroupSaga,
 );
 yield takeLatest(
  identityUpdateItemGroupStart.type,
  identityUpdateItemGroupSaga,
 );
 yield takeLatest(identityGenerateApiKeyStart.type, identityGenerateApiKeySaga);
}

